import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { dashboardService } from '../services';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CircularProgress, 
  Chip,
  Container,
  Paper,
  Avatar,
  Stack,
  Divider
} from '@mui/material';
import { 
  Church, 
  Calendar, 
  CreditCard, 
  Users, 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Activity,
  AlertCircle,
  CheckCircle,
  BarChart3
} from 'lucide-react';
import { formatCurrency, formatNumber, formatPercentage } from '../../../utils';

interface StatCardProps {
  title: string;
  value: string | number;
  description?: string;
  icon: React.ReactNode;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  color?: string;
  bgGradient?: string;
}

const StatCard: React.FC<StatCardProps> = ({ 
  title, 
  value, 
  description, 
  icon, 
  trend, 
  color = '#1976d2',
  bgGradient = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
}) => (
  <Grid item xs={12} sm={6} md={3}>
    <Card 
      sx={{ 
        height: '100%',
        background: bgGradient,
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(255,255,255,0.1)',
          backdropFilter: 'blur(10px)',
        }
      }}
    >
      <CardContent sx={{ position: 'relative', zIndex: 1, p: 2.5 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500 }}>
            {title}
          </Typography>
          <Avatar sx={{ width: 40, height: 40, bgcolor: 'rgba(255,255,255,0.2)' }}>
            {icon}
          </Avatar>
        </Box>
        
        <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>
          {value}
        </Typography>
        
        {description && (
          <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mb: 1 }}>
            {description}
          </Typography>
        )}
        
        {trend && (
          <Box display="flex" alignItems="center" gap={0.5}>
            {trend.isPositive ? 
              <TrendingUp size={14} color="rgba(76, 175, 80, 1)" /> : 
              <TrendingDown size={14} color="rgba(244, 67, 54, 1)" />
            }
            <Typography 
              variant="caption" 
              sx={{ 
                color: trend.isPositive ? 'rgba(76, 175, 80, 1)' : 'rgba(244, 67, 54, 1)',
                fontWeight: 600
              }}
            >
              {formatPercentage(Math.abs(trend.value))} vs mês anterior
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  </Grid>
);

const MetricCard: React.FC<{
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  chipLabel: string;
  chipColor: 'success' | 'warning' | 'info' | 'error';
}> = ({ title, value, subtitle, icon, chipLabel, chipColor }) => (
  <Grid item xs={12} md={4}>
    <Paper 
      elevation={2}
      sx={{ 
        p: 3, 
        height: '100%',
        borderRadius: 2,
        border: '1px solid rgba(0,0,0,0.08)',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
        }
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light' }}>
            {icon}
          </Avatar>
          <Typography variant="h6" fontWeight="600">
            {title}
          </Typography>
        </Box>
        <Chip label={chipLabel} color={chipColor} size="small" />
      </Box>
      
      <Typography variant="h3" fontWeight="bold" color="primary.main" sx={{ mb: 1 }}>
        {value}
      </Typography>
      
      {subtitle && (
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Paper>
  </Grid>
);

const Dashboard: React.FC = () => {
  const { data: dashboardData, isLoading, error } = useQuery({
    queryKey: ['dashboard-summary'],
    queryFn: dashboardService.getSummary,
  });

  if (isLoading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
          <CircularProgress size={50} />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <AlertCircle size={48} color="#f44336" />
            <Typography variant="h6" color="error" sx={{ mt: 2, mb: 1 }}>
              Erro ao carregar dados
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Tente novamente mais tarde.
            </Typography>
          </Paper>
        </Box>
      </Container>
    );
  }

  const { summary, growth } = dashboardData?.data || {};

  if (!summary) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
          <Typography variant="h6" color="text.secondary">
            Nenhum dado disponível
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Dashboard Administrativo
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Visão geral da plataforma de ingressos para paróquias
        </Typography>
      </Box>

      {/* Estatísticas Principais */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <StatCard 
          title="Total de Paróquias" 
          value={formatNumber(summary.totalParishes)} 
          description={`${formatNumber(summary.activeParishes)} ativas`} 
          icon={<Church size={20} />}
          bgGradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
        />
        <StatCard 
          title="Total de Eventos" 
          value={formatNumber(summary.totalEvents)} 
          description={`${formatNumber(summary.publishedEvents)} publicados`} 
          icon={<Calendar size={20} />}
          bgGradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
        />
        <StatCard 
          title="Total de Usuários" 
          value={formatNumber(summary.totalUsers)} 
          description="Usuários registrados" 
          icon={<Users size={20} />}
          bgGradient="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
        />
        <StatCard 
          title="Inscrições Pagas" 
          value={formatNumber(summary.paidRegistrations)} 
          description={`de ${formatNumber(summary.totalRegistrations)} total`} 
          icon={<Activity size={20} />}
          bgGradient="linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)"
        />
      </Grid>

      {/* Métricas Financeiras */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <StatCard 
          title="Receita Total" 
          value={formatCurrency(summary.totalRevenue)} 
          description="Valor total arrecadado" 
          icon={<DollarSign size={20} />} 
          trend={{ value: growth?.revenueGrowth || 0, isPositive: (growth?.revenueGrowth || 0) >= 0 }}
          bgGradient="linear-gradient(135deg, #fa709a 0%, #fee140 100%)"
        />
        <StatCard 
          title="Taxa da Plataforma" 
          value={formatCurrency(summary.totalPlatformFees)} 
          description="Receita da plataforma" 
          icon={<CreditCard size={20} />}
          bgGradient="linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)"
        />
        <StatCard 
          title="Ticket Médio" 
          value={formatCurrency(summary.averageTransaction)} 
          description="Valor médio por transação" 
          icon={<BarChart3 size={20} />} 
          trend={{ value: growth?.transactionGrowth || 0, isPositive: (growth?.transactionGrowth || 0) >= 0 }}
          bgGradient="linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)"
        />
      </Grid>

      {/* Métricas Detalhadas */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <MetricCard
          title="Paróquias Pendentes"
          value={formatNumber(summary.pendingParishes || 0)}
          subtitle="Paróquias aguardando aprovação"
          icon={<AlertCircle size={18} />}
          chipLabel="Pendente"
          chipColor="warning"
        />
        <MetricCard
          title="Taxa de Conversão"
          value={summary.totalRegistrations > 0 ? formatPercentage((summary.paidRegistrations / summary.totalRegistrations) * 100) : '0%'}
          subtitle={`${formatNumber(summary.paidRegistrations)} de ${formatNumber(summary.totalRegistrations)} inscrições`}
          icon={<TrendingUp size={18} />}
          chipLabel="Conversão"
          chipColor="info"
        />
        <MetricCard
          title="Eventos Ativos"
          value={formatNumber(summary.publishedEvents)}
          subtitle={`${summary.totalEvents > 0 ? formatPercentage((summary.publishedEvents / summary.totalEvents) * 100) : '0%'} do total de eventos`}
          icon={<CheckCircle size={18} />}
          chipLabel="Ativo"
          chipColor="success"
        />
      </Grid>

      {/* Atividade Recente */}
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" fontWeight="600" gutterBottom>
          Atividade Recente
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Últimas ações realizadas na plataforma
        </Typography>
        
        <Stack spacing={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'success.light' }}>
              <CheckCircle size={16} />
            </Avatar>
            <Box flex={1}>
              <Typography variant="body2" fontWeight="500">
                Sistema inicializado
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Dashboard carregado com sucesso
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              agora
            </Typography>
          </Box>
          
          <Divider />
          
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', py: 1 }}>
            Logs de atividade detalhados estão disponíveis na seção de Relatórios
          </Typography>
        </Stack>
      </Paper>
    </Container>
  );
};

export default Dashboard;

